/**
 * Pure form <-> settings mapping for the PDF generator page's letterhead
 * assignments.
 *
 * The persisted list is rebuilt from the rendered rows, so the mapping between
 * a stored assignment, the row showing it and the entry written back is the
 * whole pruning argument. It lives here to be tested directly: an earlier
 * revision kept a broken assignment's stale id in the form while displaying it
 * as unassigned, which re-persisted on save the entry the page promised to
 * prune.
 */

// "" is the row's "the organisation's default" option. It is distinct from an
// assignment: an unassigned template resolves the default at generation time,
// so a default changed later follows, where a pinned id would not.
export const NO_ASSIGNMENT = "";

export type TemplateLetterheadValues = {
  assignments: Array<{
    pdfGeneratorTemplateId: string;
    pdfLetterheadId: string;
  }>;
};

type AssignableTemplate = { id: string; pdfLetterheadId?: string | null };
type OfferedLetterhead = { id: string };

/**
 * A stored assignment naming a letterhead the organisation no longer has enters
 * the form as NO_ASSIGNMENT rather than as its stale id, because the form value
 * is what gets written back — carrying the stale id here is what would
 * re-persist it. The row still renders as broken rather than as unassigned;
 * that reads the stored value, via `hasBrokenStoredAssignment`.
 */
export function templateAssignmentInitialValues(
  templates: AssignableTemplate[],
  letterheads: OfferedLetterhead[]
): TemplateLetterheadValues {
  return {
    assignments: templates.map(template => {
      const assigned = template.pdfLetterheadId ?? NO_ASSIGNMENT;
      return {
        pdfGeneratorTemplateId: template.id,
        pdfLetterheadId: letterheads.some(
          letterhead => letterhead.id === assigned
        )
          ? assigned
          : NO_ASSIGNMENT,
      };
    }),
  };
}

/** What is written back: the rendered rows, minus the ones meaning "default". */
export function toTemplateLetterheadsSlice(
  values: TemplateLetterheadValues
): TemplateLetterheadValues["assignments"] {
  return values.assignments.filter(
    assignment => assignment.pdfLetterheadId !== NO_ASSIGNMENT
  );
}

/**
 * The template holds an assignment naming a letterhead that is gone. Generation
 * refuses such a template rather than composing the default, so the row must not
 * look like the harmless unassigned case.
 */
export function hasBrokenStoredAssignment(
  template: AssignableTemplate,
  letterheads: OfferedLetterhead[]
): boolean {
  const assigned = template.pdfLetterheadId ?? NO_ASSIGNMENT;
  return (
    assigned !== NO_ASSIGNMENT &&
    !letterheads.some(letterhead => letterhead.id === assigned)
  );
}

/**
 * The template ids the stored settings hold assignments for, or null when the
 * blob cannot be read as an assignment list.
 *
 * Null means "unknown", not "none": the caller must not conclude from it that
 * nothing is stored. The settings arrive as an opaque JSON scalar, so this
 * narrows it defensively rather than trusting its shape.
 */
export function storedAssignmentTemplateIds(
  settings: unknown
): string[] | null {
  if (typeof settings !== "object" || settings === null) return null;
  const held = (settings as { templateLetterheads?: unknown })
    .templateLetterheads;
  // Absent is legitimately empty — an organisation connected before this
  // capability existed holds no key at all.
  if (held === undefined) return [];
  if (!Array.isArray(held)) return null;

  const ids: string[] = [];
  for (const entry of held) {
    if (typeof entry !== "object" || entry === null) return null;
    const id = (entry as { pdfGeneratorTemplateId?: unknown })
      .pdfGeneratorTemplateId;
    if (typeof id !== "string") return null;
    ids.push(id);
  }
  return ids;
}

/**
 * Whether saving must be offered even though the form matches what it displays.
 *
 * Both cases are ones where the *stored* list differs from what the rows would
 * persist, so pruning is the only thing a save does — and without this the save
 * row, which follows dirtiness, would never appear to perform it:
 *
 * - a broken assignment, which enters the form already coerced to "default";
 * - an assignment for a template the service no longer reports, which the rows
 *   cannot show at all and which saving therefore drops.
 *
 * The second case needs the stored ids, not just the reported ones. An earlier
 * revision asked whether the reported list was *empty*, which only caught the
 * all-templates-gone case: with one template vanished among several sound ones,
 * no row is broken and the list is non-empty, so nothing offered the save that
 * would prune it — and that hidden entry still blocks its letterhead's
 * deletion, with no way to clear it from this page.
 *
 * A null `storedTemplateIds` means the stored list could not be read, so
 * staleness cannot be judged and no save is offered on those grounds.
 *
 * Only ever called on a successful read of the templates; a failed one renders
 * no form at all.
 */
export function offersSaveWhilePristine(
  templates: AssignableTemplate[],
  letterheads: OfferedLetterhead[],
  storedTemplateIds: string[] | null
): boolean {
  if (
    templates.some(template => hasBrokenStoredAssignment(template, letterheads))
  ) {
    return true;
  }
  if (storedTemplateIds === null) return false;

  const reported = new Set(templates.map(template => template.id));
  return storedTemplateIds.some(id => !reported.has(id));
}
